// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A mark at the pinch point, where the room does not show the hands (M13-28).
//
// From the second device day of M13: "hands are tracking but not showing the hands." In mixed
// reality the passthrough shows the author's real hand and the ghost hand is drawn over it; in the
// arcade room there is no passthrough, so what the author has is the ghost — a translucent hand
// whose thumb and index tip meet somewhere the eye has to guess at. A pinch lands on the plinth
// under *that* point (`HandReport.pinch`, the point the carry is made with), so this draws it: one
// small unlit dot per tracked hand, through everything as the halo is, brighter and a little
// larger while the hand is pinched. Nothing for a controller (its grip is a thing the author can
// see and feel), nothing while the passthrough shows the real hand, nothing in a room with no
// hands. The smallest honest answer, not a hand model — the ghost hands are that.
//
// Pure Three: no runtime, no clock. `update` is told whether the marks are wanted at all, so the
// decision about which look shows a real hand stays with the runtime that knows the look.

import { Group, Mesh, MeshBasicMaterial, SphereGeometry } from 'three'
import type { HandReport } from '../xr/xrInput'

/** The dot's radius, metres: a pinhead, not a fingertip. */
export const PINCH_MARK_RADIUS = 0.005
/** How much larger the dot is while pinched. */
export const PINCH_MARK_PINCHED_SCALE = 1.6
/** The ghost hands' blue-white, so it reads as part of the same hologram. */
export const PINCH_MARK_COLOUR = 0xbfe3ff
export const PINCH_MARK_IDLE_OPACITY = 0.45
export const PINCH_MARK_PINCHED_OPACITY = 0.95

export interface PinchMarksBuild {
  readonly group: Group
  /** Both hands, this frame; `wanted` is false where the real hand is visible (passthrough) or there is no session. */
  update(left: HandReport, right: HandReport, wanted: boolean): void
  dispose(): void
}

export function buildPinchMarks(): PinchMarksBuild {
  const group = new Group()
  group.name = 'pinch-marks'
  const geometry = new SphereGeometry(PINCH_MARK_RADIUS, 12, 8)
  const marks = (['left', 'right'] as const).map((handedness) => {
    const material = new MeshBasicMaterial({
      color: PINCH_MARK_COLOUR,
      transparent: true,
      opacity: PINCH_MARK_IDLE_OPACITY,
      // Through everything, as the halo is: the point is *where the pinch is*, and a dot hidden
      // behind the part it is over is a dot that answers nothing.
      depthTest: false,
      depthWrite: false,
    })
    const mesh = new Mesh(geometry, material)
    mesh.name = `pinch-mark-${handedness}`
    mesh.renderOrder = 1001
    mesh.visible = false
    group.add(mesh)
    return { mesh, material }
  })

  const place = (mark: (typeof marks)[number], hand: HandReport, wanted: boolean): void => {
    const shown = wanted && hand.present && hand.kind === 'hands'
    mark.mesh.visible = shown
    if (!shown) return
    mark.mesh.position.set(hand.pinch.x, hand.pinch.y, hand.pinch.z)
    const pinched = hand.grab
    mark.material.opacity = pinched ? PINCH_MARK_PINCHED_OPACITY : PINCH_MARK_IDLE_OPACITY
    const scale = pinched ? PINCH_MARK_PINCHED_SCALE : 1
    mark.mesh.scale.set(scale, scale, scale)
  }

  return {
    group,
    update(left, right, wanted) {
      place(marks[0]!, left, wanted)
      place(marks[1]!, right, wanted)
    },
    dispose() {
      for (const mark of marks) mark.material.dispose()
      geometry.dispose()
    },
  }
}
